import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppStore } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import {
  IElementsState,
  selectIsSubtreeLoaded,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";

/**
 * A hook to determine which of the given root ids have not been fetched already.
 * These ids are not updated every time the store is updated, on purpose.
 *
 * @param ids ids of root elements whose subtrees should be fetched
 * @returns A subset of the given ids, containing only those whose subtree is not yet loaded
 */
export function useIDsToFetch(ids: GUID[] | undefined): GUID[] {
  const { getState } = useAppStore();
  return useMemo(() => {
    const state = getState();
    return (
      ids?.filter((id) => selectIsSubtreeLoaded(id)(state) === false) ?? []
    );
  }, [ids, getState]);
}

/**
 * A memoized selector to determine if all the given root ids have their subtree loaded.
 *
 * @param state The application's store current state
 * @param ids ids of root elements whose subtrees should be checked
 * @returns true if all the given ids have their subtree loaded, false otherwise
 */
export const selectAllSubtreesLoaded = curryAppSelector(
  createSelector(
    [selectIElementsSlice, (state: RootState, ids: GUID[]) => ids],
    computeAllSubtreesLoaded,
  ),
);

function computeAllSubtreesLoaded(
  iElements: IElementsState,
  ids: GUID[],
): boolean {
  if (ids.length === 0) return true;
  const state = { iElements };
  return ids.every((id) => selectIsSubtreeLoaded(id)(state));
}
